import { Box } from '@astro-ui/components/Box'
import { Typography } from '@astro-ui/components/Typography'
import { dsColor } from '@astro-ui/fondations/colors'

/** `small` is the Figma "Label/ Small" chip; `medium` the multi-select value. */
export type SelectedChipsValueSizeType = 'small' | 'medium'

export type SelectedChipsValuePropsType = {
  labels: string[]
  /** Chips shown before the rest collapse into `+N`. */
  maxVisible?: number
  size?: SelectedChipsValueSizeType
}

const SIZE_STYLE: Record<
  SelectedChipsValueSizeType,
  { chipGap: string; padding: string; fontSize: string; lineHeight: string; fontWeight: number }
> = {
  small: {
    chipGap: '4px',
    padding: '2px 4px',
    fontSize: '10px',
    lineHeight: '12px',
    fontWeight: 700,
  },
  medium: {
    chipGap: '6px',
    padding: '2px 6px',
    fontSize: '12px',
    lineHeight: '18px',
    fontWeight: 600,
  },
}

type ChipPropsType = {
  label: string
  size: SelectedChipsValueSizeType
  isCounter?: boolean
}

const Chip = ({ label, size, isCounter = false }: ChipPropsType) => {
  const { padding, fontSize, lineHeight, fontWeight } = SIZE_STYLE[size]

  return (
    <Box
      flexShrink={isCounter ? 0 : 1}
      minWidth={0}
      padding={padding}
      borderRadius="4px"
      backgroundColor={dsColor.neutral['neutral-2']}
    >
      <Typography
        display="block"
        fontSize={fontSize}
        lineHeight={lineHeight}
        fontWeight={fontWeight}
        color={dsColor.neutral['neutral-8']}
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {label}
      </Typography>
    </Box>
  )
}

/**
 * Single-row summary of a multi-select value: plain chips (removal happens in
 * the dropdown) and a `+N` counter for the rest. Renders nothing when empty so
 * the Select falls back to its placeholder.
 */
const SelectedChipsValue = ({
  labels,
  maxVisible = 4,
  size = 'medium',
}: SelectedChipsValuePropsType) => {
  if (!labels.length) return null

  const visible = labels.slice(0, maxVisible)
  const hiddenCount = labels.length - visible.length

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={SIZE_STYLE[size].chipGap}
      minWidth={0}
      overflow="hidden"
    >
      {visible.map((label, index) => (
        // Labels can repeat (two hubs sharing a name); the order is stable per render.
        // eslint-disable-next-line react/no-array-index-key
        <Chip key={`${label}-${index}`} label={label} size={size} />
      ))}
      {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} size={size} isCounter />}
    </Box>
  )
}

export default SelectedChipsValue
